/*
 * @providesModule SKZBracket
 *
 *
 * Created by Skillz on 2/23/16.
 *   Copyright © 2016 Skillz. All rights reserved.
 *
 */

import _ from 'lodash';
import I18n from 'INTL';
import Match, {MatchRecord} from 'SKZMatch';
import SKZAPIConstants from 'SKZAPIConstants';

import {NativeModules} from 'react-native';

const SKZAPIManager = NativeModules.SKZAPIManager;

export const BRACKET_STATUS = {
  OPEN: 'OPEN',
  FULL: 'FULL',
  CONCLUDED: 'CONCLUDED',
  CANCELED: 'CANCELED',
} as const;

export type BracketState = (typeof BRACKET_STATUS)[keyof typeof BRACKET_STATUS];

export const BRACKET_PLAYER_STATE = {
  READY: 'READY',
  PENDING: 'PENDING',
  FINISHED: 'FINISHED',
} as const;

export type BracketPlayerState = (typeof BRACKET_PLAYER_STATE)[keyof typeof BRACKET_PLAYER_STATE];

export class BracketRecord {
  id: string;
  size: number;
  num_entries: number;
  tournament_id: string;
  updated_at: string;
  cancel_at: string;
  state: BracketState;

  player: {
    id: string;
    user_id: number;
    created_at: string;
    updated_at: string;
    expires_at: string;
    state: BracketPlayerState;

    starting_position: number;
    latest_position: number;
  };

  matches: MatchRecord[] = [];
  current_player_info!: any;
}

export default class Bracket extends BracketRecord {
  matches: Match[] = [];

  constructor(bracketRecord: BracketRecord) {
    super();

    const newBracket = _.create(Bracket.prototype, bracketRecord);
    newBracket.matches = Match.make(_.get(bracketRecord, 'matches', []));
    return newBracket;
  }

  getUserId(): number {
    return this.player.user_id;
  }

  getUsersLastPosition(): number {
    return _.get(this, 'player.latest_position', this.size + 1);
  }

  getUsersLastMatch(): Match {
    const usersLastMatch = _.filter(this.matches, (match: Match): boolean => {
      return match.bracket_position == this.getUsersLastPosition();
    })[0];

    return new Match(usersLastMatch);
  }

  getLastMatchForUser(userId: string): Match {
    let match = _.find(this.matches, 'bracket.position', this.player.latest_position);
    if (!match && this.matches instanceof Array) {
      this.matches.forEach((bMatch: Match) => {
        const player = _.find(bMatch.players, (p: Object): boolean => p.user_id == userId);

        if (player && (!match || bMatch.bracket.position > match.bracket.position)) {
          match = bMatch;
        }
      });
    }

    return new Match(match);
  }

  isFinished(): boolean {
    return this.state == 'CONCLUDED' || this.state == 'CANCELED';
  }

  isCanceled(): boolean {
    return this.state == 'CANCELED';
  }

  didWinChampionship(): boolean {
    const usersLastMatch: Match = this.getUsersLastMatch();

    if (!usersLastMatch) {
      return false;
    }
    const didWin =
      Bracket.isChampionship(this.getUsersLastPosition()) &&
      usersLastMatch.didWinMatch(this.getUserId()) &&
      !usersLastMatch.isAborted(this.getUserId());

    return didWin;
  }

  /**
   * Calculates the number of rounds in a bracket, given its size
   */
  static calculateNumRounds(bracketSize: number): number {
    return Math.floor(Math.log2(bracketSize));
  }

  /**
   * Calculates the current round, given a position
   */
  static calculateRound(position: number): number {
    const bracketSize = Bracket.calculateBracketSize(position);
    return Math.log2(bracketSize) - Math.floor(Math.log2(bracketSize - (position - bracketSize)));
  }

  /**
   * Calculates the next available position based on the current number of entries.
   * This is only going to work in Round 1.
   */
  static calculateFirstRoundTournamentPosition(numEntries: number, bracketSize: number): number {
    return bracketSize + 1 + Math.floor(numEntries / 2);
  }

  /**
   * Calculates the bracket size, given a position
   */
  static calculateBracketSize(position: number): number {
    return Math.pow(2, Math.floor(Math.log2(position)));
  }

  /**
   * Calculates how many tournaments are in a round, given the round and bracket size
   */
  static calculateRoundSize(round: number, bracketSize: number): number {
    const totalNumRounds = Bracket.calculateNumRounds(bracketSize);
    return Math.pow(2, totalNumRounds - round);
  }

  /**
   * Calculates the position of the next tournament in the bracket, given a position.
   * The next tournament is the tournament that the winner of the current position will
   * advance to.
   */
  static calculateNextPosition(position: number): number {
    if (Bracket.isChampionship(position)) {
      return 0;
    }

    const bracketSize = Bracket.calculateBracketSize(position);
    return position + Math.floor((bracketSize * 2 + 1 - position) / 2);
  }

  // returns the lower numbered previous positiohn
  static previousPosition(position: number): number {
    const bracketSize = Bracket.calculateBracketSize(position);
    const previousPosition = position - (bracketSize * 2 - position) - 1;

    return previousPosition; // previousPosition+1 is the other previous position
  }
  /**
   * Returns true if the position is the final tournament in the bracket
   */
  static isChampionship(position: number): boolean {
    return position == Bracket.calculateChampionshipFromPosition(position);
  }

  /**
   * Returns the last position in the bracket based on a position
   */
  static calculateChampionshipFromPosition(position: number): number {
    return Bracket.calculateChampionshipFromBracketSize(Bracket.calculateBracketSize(position));
  }

  /**
   * Returns the last position in the bracket based on a bracket size
   */
  static calculateChampionshipFromBracketSize(bracketSize: number): number {
    return bracketSize * 2 - 1;
  }

  /**
   * Figures out the round of the given position, and then returns the position
   * of the last tournament in that same round.
   */
  static calculateLastPositionInRound(position: number): number {
    const bracketSize = Bracket.calculateBracketSize(position);
    const currentRound = Bracket.calculateRound(position);

    let sumOfRoundSizes = 0;
    for (let i = 1; i <= currentRound; i++) {
      sumOfRoundSizes += Bracket.calculateRoundSize(i, bracketSize);
    }

    return sumOfRoundSizes + bracketSize;
  }

  static calculateLastPositionInRoundOne(bracketSize: number): number {
    const ONE_POINT_FIVE = 1.5;

    return bracketSize * ONE_POINT_FIVE;
  }

  /**
   * Returns the prize rank for the prize rules at the given position.
   */
  static calculatePrizeRankAtPosition(position: number): number {
    return Bracket.calculateChampionshipFromPosition(position) - position + 1;
  }

  /**
   * Returns the position at the given prize rank
   */
  static calculatePositionAtPrizeRank(prizeRank: number, bracketSize: number): number {
    return Bracket.calculateChampionshipFromBracketSize(bracketSize) - prizeRank + 1;
  }

  static calculateRoundSizeForPosition(position: number): number {
    const round = this.calculateRound(position);
    const bracketSize = this.calculateBracketSize(position);
    const roundSize = this.calculateRoundSize(round, bracketSize);
    return roundSize;
  }

  static textForPosition(position: number): string {
    if (position == 1) {
      return I18n.message('Winner');
    } else if (position == 2) {
      return I18n.message('Finalist');
    }

    return I18n.formattedMessage('Top {0}', this.calculateRoundSizeForPosition(position));
  }

  static getBracketBaseURL(bracketId: string): string {
    const defaultURL = 'http://www.google.com';
    let env = '';
    switch (SKZAPIManager.CurrentEnvironment) {
      case SKZAPIManager.Environment.Prod:
        env = SKZAPIConstants.SKZ_API_BASE_PROD;
        break;
      case SKZAPIManager.Environment.QA:
        env = SKZAPIConstants.SKZ_API_BASE_QA;
        break;
      case SKZAPIManager.Environment.Sandbox:
        env = SKZAPIConstants.SKZ_API_BASE_SAND;
        break;
      case SKZAPIManager.Environment.Staging:
        env = SKZAPIConstants.SKZ_API_BASE_STAGE;
        break;
      case SKZAPIManager.Environment.ProxyTS:
        env = SKZAPIConstants.SKZ_API_BASE_PROXY_TS;
        break;
      default:
        env = defaultURL;
    }

    const baseURL = 'https://replay.' + env + 'brackets/' + bracketId;
    return baseURL;
  }

  static numberOfPlayersInBracketRound(position: number): number {
    const numMatchesInRound = this.calculateRoundSizeForPosition(position);
    const playersInRound = numMatchesInRound * 2;

    return playersInRound;
  }

  /**
   * Calculates the number of pending matches in a given round.
   * It first calculates the range of positions for the matches in the specified round(first and last position).
   * It stores a map of the matches in the bracket, with the key being the position of the match and bool value of whether the match is completed.
   * Then it iterates through the matches in the range of positions  and counts the number of matches that are not completed.
   * @param {number} position
   * @returns  {number} The number of pending matches in the round
   */
  calculateNumberOfPendingMatchesInRound(position: number): number {
    const numMatchesInRound = Bracket.calculateRoundSizeForPosition(position);
    const lastMatchPosition = Bracket.calculateLastPositionInRound(position);
    const firstMatchPosition = lastMatchPosition - numMatchesInRound + 1;

    const matchesMap = this.matches.reduce((acc, match) => {
      return acc.set(match.bracket?.position, match.isCompleted());
    }, new Map());

    let pendingMatches = 0;
    for (let i = firstMatchPosition; i <= lastMatchPosition; i++) {
      const isCompleted = matchesMap.get(i);
      if (!isCompleted) {
        pendingMatches++;
      }
    }

    return pendingMatches;
  }

  /**
   * Calculates the user's current position in the bracket.
   * It first checks if the player is in pending state, if so, it uses the position of the last match they played.
   * Otherwise, it uses the user's latest position in the bracket.
   * @param {string} bracketMatchId
   * @returns  {number} The user's current position in the bracket
   */
  getUsersLastPlayedPosition(bracketMatchId?: string): number {
    const isPlayerPending = this.player.state === 'PENDING';
    const lastPlayedMatch = this.matches.find((match) => match.id === bracketMatchId);
    const lastPosition = lastPlayedMatch?.bracket?.position ?? this.getUsersLastPosition();
    const userPosition = isPlayerPending ? lastPosition : this.getUsersLastPosition();

    return userPosition;
  }

  /**
   * Calculates the number of pending matches for a giver bracket match
   * It first calculates the user position, based on the player state.
   * Then it calls calculateNumberOfPendingMatchesInRound with the user position.
   * @param {string} bracketMatchId
   * @returns  {number} The number of matches left
   */
  getNextRoundDescription(bracketMatchId?: string): string {
    const userPosition = this.getUsersLastPlayedPosition(bracketMatchId);
    const matchesLeft = this.calculateNumberOfPendingMatchesInRound(userPosition);

    return I18n.formattedMessage(
      `Next Round: ${matchesLeft} ${matchesLeft === 1 ? 'match' : 'matches'} left`
    );
  }
}
